import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import RolesDialog from './RolesDialog';
import RolesFilterForm from './RolesFilterForm';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { isPermissionCodeValid } from '../../../utils/mics';
// import PropConfigFilter from './PropConfigFilter';

const RolesList  = ({ pageId }) => {
    
    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`);

    const dispatch = useDispatch();

     const [deleteRecord, setDeleteRecord] = useState(null); // Store record for deletion

    const { rolesList, rolesDetails } = useSelector((state) => state.roles);

    const [rolesData, setRolesData] = useState([]);

    useEffect(() => {
        if (rolesList && Array.isArray(rolesList?.data) && rolesList?.data.length > 0) {
            // Update state with the processed data
            setRolesData({...rolesList, data: rolesList.data[0]?.propValue?.roles, totalRecords:  rolesList.data[0]?.propValue?.roles?.length || rolesList.totalRecords} || {});
        } else {
            // Reset or clear data if no valid rolesList
            setRolesData([]);
        }
    }, [rolesList]);

    const defaultParams = useMemo(() => ({
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder: -1,
        propCode : 'MFT.Roles',
        propGroup: 'Auth'
    }), []);

    const [formValues, setFormValues] = useState(defaultParams);
    const [isDialogVisible, setDialogVisible] = useState(false);
    const [isAddNew, setAddNew] = useState(false);
    const [selectedRoles, setSelectedRoles] = useState(null);
    
    const handleReset = () => {
        // Reset the form values to default
        setFormValues(defaultParams);
        
        // Reset rolesData to the full user list from rolesList
        setRolesData({
            ...rolesList,
            data: rolesList?.data[0]?.propValue?.users,
            totalRecords: rolesList?.data[0]?.propValue?.users?.length || rolesList.totalRecords
        });
    };
    
    useEffect(() => {
        dispatch(appActions.ROLES_GET_ALL_REQUEST(defaultParams));
    }, [dispatch, defaultParams]);

    const handleRefresh = useCallback(() => {
        dispatch(appActions.ROLES_GET_ALL_REQUEST(defaultParams));
    }, [dispatch, defaultParams]);

    useEffect(() => {
        if (rolesDetails?.action === 'delete') {
            handleRefresh();
        }
    }, [rolesDetails, handleRefresh]);

    const handleSearch = useCallback(
        (params) => {
            const filteredUsers = rolesList?.data[0]?.propValue?.roles?.filter((role) => {
                let matches = true;
    
                if (params.name) {
                    matches = role.name.toLowerCase().includes(params.name.toLowerCase());
                }
                if (matches && params.code) {
                    matches = role.code.toLowerCase().includes(params.code.toLowerCase());
                }
                // Additional conditions can be added as per requirements
                return matches;
            });
    
            setRolesData({
                ...rolesList,
                data: filteredUsers,
                totalRecords: filteredUsers.length || 0
            });
        },
        [rolesList],
    );
    

    const linkToNew = useCallback(() => {
        setSelectedRoles({}); // Initialize with empty object for new user
        setAddNew(true)
        setDialogVisible(true); // Open dialog for adding new user
    }, []);

    const handleViewRedirect = useCallback(
        (record) => {
            const data = rolesData?.data?.find((t) => t.code === record.code);
            setSelectedRoles({ ...data, isView: true });
            setAddNew(false)
            setDialogVisible(true);
        },
        [rolesData], // dependencies
    );

    const handleEditRedirect = useCallback(
        (record) => {
            const data = rolesData?.data?.find((t) => t.code === record.code);
            setSelectedRoles(data);
            setAddNew(false)
            setDialogVisible(true);
        },
        [rolesData], // dependencies
    );

    const handleSaveUser = (updatedRole) => {

        const roleCode = updatedRole.code;
    
        // Update local state
        setRolesData(prevUsersData => {

            const existingUserIndex = prevUsersData?.data?.findIndex(role => role.code === roleCode);
            
            const updatedData = existingUserIndex > -1 ? prevUsersData.data.map((role, index) => index === existingUserIndex ? updatedRole : role ) : [...(prevUsersData.data || []), updatedRole];

            // Construct updatedUserRequest with the new state
            const updatedUserRequest = {
                id: rolesList?.data[0]?.id,
                propCode: rolesList?.data[0]?.propCode,
                propGroup: rolesList?.data[0]?.propGroup,
                propValue: { roles: updatedData }
            };
    
            // Dispatch action with the updated request
            dispatch(appActions.ROLES_UPDATE_REQUEST(updatedUserRequest));
    
            // Close dialog
            setAddNew(false)
            setDialogVisible(false);
    
            // Return updated state to ensure it is used correctly
            return { ...prevUsersData, data: updatedData };
        });
    };
    
    

    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
           
            const { roleCode } = deleteRecord;

            setRolesData(prevUsersData => {
                // Filter the user out of the list
                const updatedUserList = prevUsersData?.data?.filter((role) => role?.code !== roleCode);
        
                // Construct the updatedUserRequest using the filtered list
                const updatedUserRequest = {
                    id: rolesList?.data[0]?.id,
                    propCode: rolesList?.data[0]?.propCode,
                    propGroup: rolesList?.data[0]?.propGroup,
                    propValue: { roles : updatedUserList }
                };
        
                // Dispatch action with the updated request
                dispatch(appActions.USERS_UPDATE_REQUEST(updatedUserRequest));
        
                // Return updated state to ensure it is used correctly
                return { ...prevUsersData, data: updatedUserList };
            });

            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh, rolesList])    

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex"> Roles </h1>
                <Button icon="pi pi-sync" rounded text aria-label="Refresh" className='mr-auto tp' data-pr-tooltip="Refresh" onClick={handleRefresh} />
                {writeAccess &&
                    <Button label="Add New" icon="pi pi-plus" className="p-button-outlined" onClick={linkToNew} />
                }
            </div>
        );
    };

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                {readAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                {writeAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                        <i className="pi pi-pencil text-blue-500 text-base"></i>
                    </span>
                }
                {deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
                }
            </div>
        ),
        [handleViewRedirect, handleEditRedirect, handleDelete, readAccess, deleteAccess, writeAccess],
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );

    const columns = [
        { field: 'name', header: 'Role Name', sortable: true },
        { field: 'code', header: 'Role Code', sortable: true},
        { field: 'status', header: 'Status', sortable: true,width : '90px' },
        // { field: 'createdOn', header: 'Created On', sortable: true, width : '150px' , columnType : 'datetime'  },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, width : '150px', columnType : 'datetime'  },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true, width : '150px' },
    ];
    
    const renderList = () => {
        return (
            <>
                <div className="grid px-4 align-items-center my-2">
                    {renderHeader()}
                </div>
                <div className="bg-white mx-3 pv-20 ph-20 br-6 shadow-white">
                    <RolesFilterForm
                        handleSearch={handleSearch}
                        handleReset={handleReset}
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={rolesData}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='configuration'
                        isLazy={false}
                        showGenerateXmlBtn={false}
                        pageId={ pageId }
                    />

                    <RolesDialog
                        visible={isDialogVisible}
                        isAddNew={ isAddNew }
                        onHide={() => {
                            setDialogVisible(false);
                            setAddNew(false);
                        }}
                        role={selectedRoles}
                        onSave={handleSaveUser}
                    />
                </div>
            </>
        );
    };
    
    return <div className="form_height">
        {renderList()}
        {deleteRecord && (
            <ConfirmationDialog
                message={`Are you sure you want to delete the role : ${deleteRecord.code}?`}
                onAccept={handleDeleteConfirm}
                onReject={() => setDeleteRecord(null)}
            />
        )}
    </div>;
};

export default RolesList;
